// =============================================================================
// TrackerFilters.jsx
//
// Barra de filtros horizontal sempre visível acima da tabela.
//
// Filtros disponíveis:
//   - Date Range:  De / Até  (filtra actividades cujo período se sobrepõe)
//   - Technology:  multi-select (2G, 3G, 4G, 5G)
//   - Team:        multi-select (derivado das entries)
//   - Owner:       multi-select (derivado das entries)
//
// Props:
//   entries    → array completo (para derivar opções dos dropdowns)
//   filters    → { dateFrom, dateTo, technologies[], teams[], owners[] }
//   onChange   → callback(newFilters)
//   onClear    → callback() — limpa todos os filtros
//   filtered   → número de resultados após filtragem (para mostrar no badge)
// =============================================================================

import React, { useMemo } from 'react';
import {
  Box, Paper, TextField, MenuItem, Chip, Typography, Button,
  InputAdornment, Select, FormControl, InputLabel, OutlinedInput,
  Checkbox, ListItemText, Tooltip, Divider,
} from '@mui/material';
import FilterListIcon        from '@mui/icons-material/FilterList';
import ClearIcon             from '@mui/icons-material/Clear';
import { DatePicker }        from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment }     from '@mui/x-date-pickers/AdapterMoment';
import moment                from 'moment';

// Altura máx do menu dos multi-selects
const MENU_PROPS = { PaperProps: { style: { maxHeight: 300 } } };

// ── Helper: renderValue para multi-select ────────────────────────────────────
const renderChips = (selected, placeholder) =>
  selected.length === 0
    ? <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.82rem' }}>{placeholder}</Typography>
    : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((v) => (
          <Chip key={v} label={v} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
        ))}
      </Box>;

// ── Componente ────────────────────────────────────────────────────────────────
const TrackerFilters = ({ entries, filters, onChange, onClear, filtered }) => {

  // Derivar opções únicas a partir das entries carregadas
  const options = useMemo(() => {
    const techs  = [...new Set(entries.map((e) => e.technology).filter(Boolean))].sort();
    const teams    = [...new Set(entries.map((e) => e.teamName).filter(Boolean))].sort();
    const owners   = [...new Set(entries.flatMap((e) => e.ownerNames ?? []).filter(Boolean))].sort();
    const features = [...new Set(entries.map((e) => e.feature).filter(Boolean))].sort();
    const vendors  = [...new Set(entries.map((e) => e.vendor).filter(Boolean))].sort();
    return { techs, teams, owners, features, vendors };
  }, [entries]);

  const hasFilters =
    filters.dateFrom || filters.dateTo ||
    filters.technologies.length > 0 ||
    filters.teams.length > 0 ||
    filters.owners.length > 0 ||
    filters.features.length > 0 ||
    filters.vendors.length > 0;

  const totalEntries = entries.length;
  const isFiltered   = hasFilters && filtered < totalEntries;

  // ── Handler genérico para multi-selects ──────────────────────────────────
  const handleMulti = (key) => (e) => {
    const val = typeof e.target.value === 'string'
      ? e.target.value.split(',')
      : e.target.value;
    onChange({ ...filters, [key]: val });
  };

  return (
    <Box sx={{ px: 0, py: 1, mb: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>

        {/* ── Ícone + label ─────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 0.5 }}>
          <FilterListIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
            Filters
          </Typography>
        </Box>

        {/* ── Date From ─────────────────────────────────────────────────── */}
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="From"
            format="YYYY-MM-DD"
            value={filters.dateFrom ? moment(filters.dateFrom, 'YYYY-MM-DD') : null}
            onChange={(val) => onChange({ ...filters, dateFrom: val?.isValid() ? val.format('YYYY-MM-DD') : null })}
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />

          {/* ── Date To ───────────────────────────────────────────────────── */}
          <DatePicker
            label="To"
            format="YYYY-MM-DD"
            value={filters.dateTo ? moment(filters.dateTo, 'YYYY-MM-DD') : null}
            onChange={(val) => onChange({ ...filters, dateTo: val?.isValid() ? val.format('YYYY-MM-DD') : null })}
            minDate={filters.dateFrom ? moment(filters.dateFrom, 'YYYY-MM-DD') : undefined}
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />
        </LocalizationProvider>

        {/* ── Technology multi-select ───────────────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Technology</InputLabel>
          <Select
            multiple
            value={filters.technologies}
            onChange={handleMulti('technologies')}
            input={<OutlinedInput notched label="Technology" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.techs.map((t) => (
              <MenuItem key={t} value={t} dense>
                <Checkbox checked={filters.technologies.includes(t)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={t} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Team multi-select ─────────────────────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Team</InputLabel>
          <Select
            multiple
            value={filters.teams}
            onChange={handleMulti('teams')}
            input={<OutlinedInput notched label="Team" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.teams.map((t) => (
              <MenuItem key={t} value={t} dense>
                <Checkbox checked={filters.teams.includes(t)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={t} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Owner multi-select ────────────────────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Owner</InputLabel>
          <Select
            multiple
            value={filters.owners}
            onChange={handleMulti('owners')}
            input={<OutlinedInput notched label="Owner" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.owners.map((o) => (
              <MenuItem key={o} value={o} dense>
                <Checkbox checked={filters.owners.includes(o)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={o} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Type multi-select ───────────────────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Type</InputLabel>
          <Select
            multiple
            value={filters.features}
            onChange={handleMulti('features')}
            input={<OutlinedInput notched label="Type" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.features.map((f) => (
              <MenuItem key={f} value={f} dense>
                <Checkbox checked={filters.features.includes(f)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={f} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Vendor multi-select ───────────────────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Vendor</InputLabel>
          <Select
            multiple
            value={filters.vendors}
            onChange={handleMulti('vendors')}
            input={<OutlinedInput notched label="Vendor" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.vendors.map((v) => (
              <MenuItem key={v} value={v} dense>
                <Checkbox checked={filters.vendors.includes(v)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={v} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Spacer + resultado + clear ────────────────────────────────── */}
        <Box sx={{ flex: 1 }} />

        {isFiltered && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            <strong>{filtered}</strong> of {totalEntries}
          </Typography>
        )}

        {/* Clear sempre visível — desativado se não há filtros activos */}
        <Tooltip title={hasFilters ? 'Clear all filters' : 'No active filters'}>
          <span>
            <Button
              size="small"
              variant={hasFilters ? 'contained' : 'outlined'}
              color={hasFilters ? 'error' : 'inherit'}
              startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
              onClick={onClear}
              disabled={!hasFilters}
              disableElevation
              sx={{
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                minWidth: 110,
                ...(hasFilters ? {} : { color: 'text.disabled', borderColor: 'divider' }),
              }}
            >
              Clear Filters
            </Button>
          </span>
        </Tooltip>

    </Box>
  );
};

export default TrackerFilters;